import React, { useState, useEffect } from 'react';
import { Target, BookOpen, TrendingUp, Award, Loader, BarChart3 } from 'lucide-react';
import Layout from '../../Layout';
import ParentAPI from '../../services/ParentAPI';
import { showErrorNotification } from '../../utils/errorHandler';

const LearnerDomains = () => {
    const [selectedChild, setSelectedChild] = useState(null);
    const [children, setChildren] = useState([]);
    const [domainData, setDomainData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadChildren();
    }, []);

    useEffect(() => {
        if (selectedChild) {
            loadDomainData();
        }
    }, [selectedChild]);

    const loadChildren = async () => {
        try {
            const childrenData = await ParentAPI.getChildren();
            setChildren(childrenData);
            
            if (childrenData.length > 0) {
                setSelectedChild(childrenData[0]);
            }
        } catch (err) {
            console.error('Error loading children:', err);
            showErrorNotification('Failed to load children data');
        } finally {
            setLoading(false);
        }
    };

    const loadDomainData = async () => {
        if (!selectedChild) return;

        try {
            setLoading(true);
            
            // Mock domain data based on NEP 2020 framework
            const mockDomainData = {
                domains: [
                    {
                        name: 'Physical Development',
                        description: 'Development of gross and fine motor skills, health awareness, and physical fitness',
                        progress: 92,
                        goals: [
                            'Develops healthy habits',
                            'Shows coordination and balance',
                            'Demonstrates safety awareness'
                        ],
                        activities: 15,
                        competencies: 8
                    },
                    {
                        name: 'Socio-Emotional Development',
                        description: 'Building emotional intelligence, social skills, and positive relationships',
                        progress: 89,
                        goals: [
                            'Recognizes and manages emotions',
                            'Shows empathy and kindness',
                            'Cooperates with others'
                        ],
                        activities: 12,
                        competencies: 7
                    },
                    {
                        name: 'Cognitive Development',
                        description: 'Developing thinking skills, problem-solving abilities, and logical reasoning',
                        progress: 85,
                        goals: [
                            'Observes and explores environment',
                            'Solves problems creatively',
                            'Shows curiosity and wonder'
                        ],
                        activities: 18,
                        competencies: 9
                    },
                    {
                        name: 'Language & Literacy Development',
                        description: 'Building communication skills, reading, writing, and language comprehension',
                        progress: 83,
                        goals: [
                            'Communicates effectively',
                            'Shows interest in books and stories',
                            'Develops pre-reading skills'
                        ],
                        activities: 20,
                        competencies: 11
                    },
                    {
                        name: 'Aesthetic & Cultural Development',
                        description: 'Fostering creativity, artistic expression, and cultural appreciation',
                        progress: 90,
                        goals: [
                            'Expresses creativity through arts',
                            'Appreciates cultural diversity',
                            'Shows aesthetic sense'
                        ],
                        activities: 14,
                        competencies: 6
                    }
                ]
            };

            setDomainData(mockDomainData);
        } catch (err) {
            console.error('Error loading domain data:', err);
            showErrorNotification('Failed to load domain data');
        } finally {
            setLoading(false);
        }
    };

    const handleChildSwitch = (childId) => {
        const child = children.find(c => c.student_id === childId);
        setSelectedChild(child);
    };

    const getDomainColor = (index) => {
        const colors = [
            'from-blue-500 to-blue-600',
            'from-green-500 to-green-600',
            'from-purple-500 to-purple-600',
            'from-orange-500 to-orange-600',
            'from-pink-500 to-pink-600'
        ];
        return colors[index % colors.length];
    };

    const getProgressColor = (progress) => {
        if (progress >= 90) return 'text-green-600';
        if (progress >= 80) return 'text-blue-600';
        if (progress >= 70) return 'text-yellow-600';
        return 'text-red-600';
    };

    if (loading && !selectedChild) {
        return (
            <Layout pageTitle="Learner's Domains">
                <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-orange-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Domains</h2>
                        <p className="text-gray-600">Please wait while we fetch domain information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!selectedChild) {
        return (
            <Layout pageTitle="Learner's Domains">
                <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 flex items-center justify-center">
                    <div className="text-center">
                        <Target className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Child Selected</h2>
                        <p className="text-gray-600">Please select a child to view their domain development.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Learner's Domains">
            <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 p-6">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Learning Domains</h1>
                                <p className="text-gray-600">Explore {selectedChild?.name}'s development across all learning domains</p>
                            </div>
                            
                            {/* Child Selector */}
                            {children.length > 1 && (
                                <div className="relative">
                                    <select
                                        value={selectedChild?.student_id || ''}
                                        onChange={(e) => handleChildSwitch(e.target.value)}
                                        className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-2 rounded-lg font-medium appearance-none cursor-pointer pr-10"
                                    >
                                        {children.map(child => (
                                            <option key={child.student_id} value={child.student_id}>
                                                {child.name} - Grade {child.grade || 'N/A'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {domainData && (
                        <>
                            {/* Domain Overview */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {domainData.domains.map((domain, index) => (
                                    <div key={domain.name} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                                        {/* Domain Header */}
                                        <div className="flex items-center mb-4">
                                            <div className={`w-12 h-12 bg-gradient-to-br ${getDomainColor(index)} rounded-xl flex items-center justify-center mr-4`}>
                                                <Target className="w-6 h-6 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-lg font-bold text-gray-800">{domain.name}</h3>
                                                <div className={`text-2xl font-bold ${getProgressColor(domain.progress)}`}>
                                                    {domain.progress}%
                                                </div>
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-4">
                                            <div className="w-full bg-gray-200 rounded-full h-3">
                                                <div 
                                                    className={`bg-gradient-to-r ${getDomainColor(index)} h-3 rounded-full transition-all duration-500`}
                                                    style={{ width: `${domain.progress}%` }}
                                                ></div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{domain.description}</p>

                                        {/* Stats */}
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                                <div className="text-lg font-bold text-blue-600">{domain.activities}</div>
                                                <div className="text-xs text-gray-600">Activities</div>
                                            </div>
                                            <div className="text-center p-3 bg-gray-50 rounded-lg">
                                                <div className="text-lg font-bold text-purple-600">{domain.competencies}</div>
                                                <div className="text-xs text-gray-600">Competencies</div>
                                            </div>
                                        </div>

                                        {/* Goals */}
                                        <div className="mb-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Goals:</h4>
                                            <ul className="space-y-1">
                                                {domain.goals.map((goal, goalIndex) => (
                                                    <li key={goalIndex} className="text-xs text-gray-600 flex items-start">
                                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                                        {goal}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        {/* Action Button */}
                                        <button className={`w-full bg-gradient-to-r ${getDomainColor(index)} text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105`}>
                                            View Details
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Domain Insights */}
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="flex items-center mb-6">
                                    <BarChart3 className="w-8 h-8 text-orange-600 mr-3" />
                                    <h3 className="text-2xl font-bold text-gray-800">Domain Development Insights</h3>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Progress Chart */}
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-700 mb-4">Progress Overview</h4>
                                        <div className="space-y-4">
                                            {domainData.domains.map((domain, index) => (
                                                <div key={domain.name} className="flex items-center">
                                                    <div className="w-32 text-sm font-medium text-gray-700 truncate">
                                                        {domain.name}
                                                    </div>
                                                    <div className="flex-1 mx-4">
                                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                                            <div 
                                                                className={`bg-gradient-to-r ${getDomainColor(index)} h-2 rounded-full transition-all duration-500`}
                                                                style={{ width: `${domain.progress}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    <div className={`text-sm font-bold ${getProgressColor(domain.progress)} w-12 text-right`}>
                                                        {domain.progress}%
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Summary Stats */}
                                    <div>
                                        <h4 className="text-lg font-semibold text-gray-700 mb-4">Summary Statistics</h4>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-blue-50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-blue-600">
                                                    {Math.round(domainData.domains.reduce((sum, d) => sum + d.progress, 0) / domainData.domains.length)}%
                                                </div>
                                                <div className="text-sm text-gray-600">Overall Progress</div>
                                            </div>
                                            <div className="bg-green-50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-green-600">
                                                    {domainData.domains.filter(d => d.progress >= 85).length}
                                                </div>
                                                <div className="text-sm text-gray-600">Strong Domains</div>
                                            </div>
                                            <div className="bg-purple-50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-purple-600">
                                                    {domainData.domains.reduce((sum, d) => sum + d.activities, 0)}
                                                </div>
                                                <div className="text-sm text-gray-600">Total Activities</div>
                                            </div>
                                            <div className="bg-orange-50 rounded-lg p-4 text-center">
                                                <div className="text-2xl font-bold text-orange-600">
                                                    {domainData.domains.reduce((sum, d) => sum + d.competencies, 0)}
                                                </div>
                                                <div className="text-sm text-gray-600">Total Competencies</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default LearnerDomains;